import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AdminScreen, navigableScreensFor, visibleScreensFor } from './api.js';
import { getSession } from './auth.js';
import { AppShell } from './components/AppShell.js';
import { RoleGate } from './components/RoleGate.js';
import { AdminAppContext } from './context/AdminAppContext.js';
import { useAdminHttp, useAdminSession, useAdminToasts, useTenantContext } from './hooks/index.js';
import { LoginScreen } from './screens/Login.js';
import { screenDef } from './screens/registry.js';
import type { AdminSession } from './types.js';
import { omitUndefined } from '../../shared-types/src/omit-undefined.js';

const DEFAULT_SCREEN: AdminScreen = 'Overview';

export function App() {
  const { t } = useTranslation();
  const [screen, setScreen] = useState<AdminScreen>(() =>
    defaultScreenFor(getSession()?.bootstrap.membership.roles ?? [])
  );
  const baseUrl = import.meta.env.VITE_VELUGA_SERVICE_API_URL ?? '';

  const toasts = useAdminToasts(screen);
  const { clearAllToasts } = toasts;
  const resetToLogin = useCallback(() => {
    setScreen(defaultScreenFor([]));
    clearAllToasts();
  }, [clearAllToasts]);
  const resetAfterLogin = useCallback(
    (next: AdminSession) => {
      clearAllToasts();
      setScreen(defaultScreenFor(next.bootstrap.membership.roles));
    },
    [clearAllToasts]
  );
  const sessionState = useAdminSession({ onExpired: resetToLogin, onLoggedIn: resetAfterLogin });
  const http = useAdminHttp(
    baseUrl,
    sessionState.sessionRef,
    sessionState.lifecycle
  );
  const tenant = useTenantContext({
    session: sessionState.session,
    sessionRef: sessionState.sessionRef,
    lifecycle: sessionState.lifecycle,
    platformHttp: http.platformHttp,
    tenantHttp: http.tenantHttp,
    persistSession: sessionState.persistSession,
    showToast: toasts.showToast,
    t,
  });
  const roles = useMemo(
    () => sessionState.session?.bootstrap.membership.roles ?? [],
    [sessionState.session]
  );

  const navigate = useCallback(
    (next: AdminScreen) => {
      if (navigableScreensFor(roles).includes(next)) setScreen(next);
    },
    [roles]
  );

  if (!sessionState.session)
    return <LoginScreen baseUrl={baseUrl} onLoggedIn={sessionState.onLoggedIn} beginLogin={sessionState.beginLogin} notice={sessionState.notice} />;

  // The registry's requiresTenantContext drives the act-as gate; the literal
  // fallback (only Platform Console is context-free) covers an unknown id.
  const requiresTenantContext =
    screenDef(screen)?.requiresTenantContext ?? !(screen === 'Platform Console');
  const needsTenantContext =
    tenant.isCrossTenantOperator && requiresTenantContext && !tenant.selectedTenantId;

  return (
    <AdminAppContext.Provider value={{ session: sessionState, toasts, http, tenant }}>
      <AppShell
        session={sessionState.session}
        screen={screen}
        {...omitUndefined({ toast: toasts.toastByScreen[screen] })}
        tenants={tenant.tenants}
        {...omitUndefined({ selectedTenantId: tenant.selectedTenantId })}
        onScreen={navigate}
        onTenant={(tenantId) => void tenant.selectTenantContext(tenantId)}
        onDismissToast={() => toasts.dismissToast(screen)}
        onLogout={() => void sessionState.onLogout(http.rawPlatformHttp)}
      >
        <RoleGate key={`${sessionState.lifecycle.generation}:${tenant.activeTenantId ?? ''}`} roles={roles} screen={screen}>
          {needsTenantContext ? (
            <div className="panel muted">{t('appShell.selectTenantContextFirst')}</div>
          ) : (
            (screenDef(screen)?.render(omitUndefined({
              platformHttp: http.platformHttp,
              tenantHttp: tenant.tenantScopedHttp,
              activeTenantId: tenant.activeTenantId,
              selectedTenantId: tenant.selectedTenantId,
              isPlatformAdmin: tenant.isPlatformAdmin,
              isPartnerAdmin: tenant.isPartnerAdmin,
              onToast: toasts.onToastForScreen,
              onTenantContext: tenant.selectTenantContext,
            })) ?? null)
          )}
        </RoleGate>
      </AppShell>
    </AdminAppContext.Provider>
  );
}

function defaultScreenFor(roles: AdminSession['bootstrap']['membership']['roles']): AdminScreen {
  return visibleScreensFor(roles)[0] ?? DEFAULT_SCREEN;
}
